import { format } from 'date-fns';

import { getGmtOffsetString } from './getGmtOffsetString';
/**
 * ISO date format.
 * @example '2024-09-12'
 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Local date format.
 * @example '12.09.2024'
 */
export const DATE_FORMAT = 'dd.MM.yyyy';

/**
 * Date and time format without seconds.
 * @example '12.09.2024 15:30'
 */
export const DATE_TIME_SHORT_FORMAT = 'dd.MM.yyyy HH:mm';

/**
 * Formats a given date to ISO format (yyyy-MM-dd).
 * @example '2024-09-12'
 */
export const formatToISODate = (date: Date): string =>
  format(date, ISO_DATE_FORMAT);

/**
 * Formats a given date to local format (dd.MM.yyyy).
 * @example '12.09.2024'
 */
export const formatToDate = (date: Date): string => format(date, DATE_FORMAT);

/**
 * Formats a given date to date and time format without seconds (dd.MM.yyyy HH:mm).
 * @example '12.09.2024 15:30'
 */
export const formatToDateTimeShort = (date: Date): string =>
  format(date, DATE_TIME_SHORT_FORMAT);

/** * Converts a date to UTC by setting the time to midnight (00:00:00).
 * @param date - The date to convert.
 * @returns A new Date object representing the same date in UTC with the time set to midnight.
 * @example
 * const localDate = new Date('2024-09-12T15:30:00');
 * const utcDate = convertToUTC(localDate);
 * console.log(utcDate.toISOString()); // '2024-09-12T00:00:00.000Z'
 */
export const convertToUTC = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

export const toZeroTime = (date: Date) => {
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Converts a given date to UTC time.
 */
export const getUtcTime = (date: Date): Date => {
  const d = new Date(date);
  d.setTime(d.getTime() + d.getTimezoneOffset() * 60000);
  return d;
};

/**
 * Converts a given date to the specified timezone.
 * @returns {Date} The date converted to the given timezone.
 */
export const convertDateByTimezone = (date: Date, gmtOffset: number): Date => {
  const utcDate = getUtcTime(date);
  utcDate.setTime(utcDate.getTime() + gmtOffset * 1000);
  return utcDate;
};

/**
 * Formats a date and appends its GMT offset string.
 *
 * @param date - The date to format.
 * @param gmtOffset - GMT offset in seconds (for example, `18000` for GMT+5).
 * @param formatFunc - Function used to format the date part.
 * @returns The formatted date string with GMT suffix, @example: `12.09.2024 15:30 (GMT+5)`.
 *
 */
export const formatWithGmtOffset = (
  date: Date,
  gmtOffset: number,
  formatFunc: (date: Date) => string
): string => {
  const gmtOffsetString = getGmtOffsetString(gmtOffset);
  return `${formatFunc(date)} ${gmtOffsetString}`;
};
